import React from 'react';
import { Layout } from '../../components/Layout';
import { Checklist } from '../../components/Checklist';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { IDENTITY_CHECKLIST } from '../../utils/constants';
import { Shield, Key, Mail, AlertTriangle, ArrowRight, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Identity() {
  const breadcrumbs = [{
    label: 'Personal Protocol',
    path: '/personal'
  }, {
    label: 'Identity Security'
  }];
  return <Layout breadcrumbs={breadcrumbs} data-id="element-4613">
      <div className="container mx-auto px-4 py-12" data-id="element-4614">
        {/* Hero */}
        <div className="mb-12" data-id="element-4615">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-medium mb-4 border border-cyan-500/20" data-id="element-4616">
            <Shield className="h-3 w-3 mr-2" data-id="element-4617" />
            Personal Protocol / Identity
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4" data-id="element-4618">
            Identity & Account Security
          </h1>
          <p className="text-xl text-slate-400 max-w-3xl" data-id="element-4619">
            Identitas digital adalah perimeter keamanan utama. Lindungi akun
            Anda karena di situlah serangan siber modern dimulai.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4620">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-4621">
            {/* Core Principles */}
            <section data-id="element-4622">
              <h2 className="text-2xl font-bold text-slate-100 mb-6 flex items-center" data-id="element-4623">
                <Lock className="h-6 w-6 text-cyan-500 mr-3" data-id="element-4624" />
                Core Principles
              </h2>
              <div className="grid md:grid-cols-3 gap-4" data-id="element-4625">
                <Card className="p-4 bg-slate-900/50" data-id="element-4626">
                  <h3 className="font-bold text-slate-200 mb-2" data-id="element-4627">
                    Password ≠ Identity
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-4628">
                    Password saja tidak cukup. Autentikasi harus berlapis.
                  </p>
                </Card>
                <Card className="p-4 bg-slate-900/50" data-id="element-4629">
                  <h3 className="font-bold text-slate-200 mb-2" data-id="element-4630">
                    Email = Root of Trust
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-4631">
                    Siapa yang menguasai email, menguasai semua akun lainnya.
                  </p>
                </Card>
                <Card className="p-4 bg-slate-900/50" data-id="element-4632">
                  <h3 className="font-bold text-slate-200 mb-2" data-id="element-4633">
                    Passkey &gt; MFA
                  </h3>
                  <p className="text-sm text-slate-400" data-id="element-4634">
                    Gunakan teknologi terbaru yang anti-phishing.
                  </p>
                </Card>
              </div>
            </section>

            {/* Protocol Steps */}
            <section data-id="element-4635">
              <h2 className="text-2xl font-bold text-slate-100 mb-6" data-id="element-4636">
                Protocol Steps
              </h2>
              <div className="space-y-6" data-id="element-4637">
                <div className="flex gap-4" data-id="element-4638">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-cyan-500/20 text-cyan-500 flex items-center justify-center font-bold" data-id="element-4639">
                    1
                  </div>
                  <div data-id="element-4640">
                    <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4641">
                      Gunakan Password Manager
                    </h3>
                    <p className="text-slate-400 mb-2" data-id="element-4642">
                      Otak manusia tidak didesain untuk mengingat 100+ password
                      acak. Gunakan Bitwarden, 1Password, atau Apple/Google
                      Password Manager.
                    </p>
                    <ul className="list-disc list-inside text-sm text-slate-500 ml-2" data-id="element-4643">
                      <li data-id="element-4644">Password harus panjang (≥14 karakter)</li>
                      <li data-id="element-4645">Harus unik untuk setiap situs</li>
                    </ul>
                  </div>
                </div>

                <div className="flex gap-4" data-id="element-4646">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-cyan-500/20 text-cyan-500 flex items-center justify-center font-bold" data-id="element-4647">
                    2
                  </div>
                  <div data-id="element-4648">
                    <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4649">
                      Aktifkan Passkey atau MFA
                    </h3>
                    <p className="text-slate-400 mb-2" data-id="element-4650">
                      Prioritaskan Passkey jika tersedia. Jika tidak, gunakan
                      Authenticator App (TOTP) atau Hardware Key (YubiKey).
                    </p>
                    <p className="text-sm text-red-400/80 mt-1 flex items-center" data-id="element-4651">
                      <AlertTriangle className="h-3 w-3 mr-1" data-id="element-4652" />
                      Hindari SMS OTP jika ada pilihan lain.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4" data-id="element-4653">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-cyan-500/20 text-cyan-500 flex items-center justify-center font-bold" data-id="element-4654">
                    3
                  </div>
                  <div data-id="element-4655">
                    <h3 className="text-lg font-bold text-slate-200 mb-2" data-id="element-4656">
                      Amankan Email Utama
                    </h3>
                    <p className="text-slate-400" data-id="element-4657">
                      Email utama adalah kunci kerajaan. Wajib menggunakan MFA
                      terkuat dan jangan gunakan untuk mendaftar situs
                      sembarangan (gunakan alias).
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Common Mistakes */}
            <section className="border-t border-slate-800 pt-8" data-id="element-4658">
              <h2 className="text-xl font-bold text-slate-100 mb-4 text-red-400" data-id="element-4659">
                Common Mistakes
              </h2>
              <ul className="space-y-3 text-slate-400" data-id="element-4660">
                <li className="flex items-start" data-id="element-4661">
                  <AlertTriangle className="h-5 w-5 text-red-500/50 mr-3 flex-shrink-0 mt-0.5" data-id="element-4662" />
                  <span data-id="element-4663">
                    Menggunakan satu email untuk perbankan, sosmed, dan
                    newsletter sampah.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-4664">
                  <AlertTriangle className="h-5 w-5 text-red-500/50 mr-3 flex-shrink-0 mt-0.5" data-id="element-4665" />
                  <span data-id="element-4666">
                    Menyimpan password di Notes atau Excel tanpa enkripsi.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-4667">
                  <AlertTriangle className="h-5 w-5 text-red-500/50 mr-3 flex-shrink-0 mt-0.5" data-id="element-4668" />
                  <span data-id="element-4669">
                    Hanya mengandalkan SMS untuk verifikasi 2 langkah (rentan
                    SIM Swap).
                  </span>
                </li>
              </ul>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4670">
            <Checklist id="identity-checklist" title="Identity Checklist" items={IDENTITY_CHECKLIST} data-id="element-4671" />

            <Card className="p-6 bg-slate-900/50 border-slate-800" data-id="element-4672">
              <h3 className="font-bold text-slate-100 mb-4" data-id="element-4673">Next Steps</h3>
              <div className="space-y-3" data-id="element-4674">
                <Link to="/devices/ios" data-id="element-4675">
                  <Button variant="secondary" fullWidth className="justify-between group" data-id="element-4676">
                    Secure iOS Devices
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-4677" />
                  </Button>
                </Link>
                <Link to="/devices/android" data-id="element-4678">
                  <Button variant="secondary" fullWidth className="justify-between group" data-id="element-4679">
                    Secure Android Devices
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-4680" />
                  </Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
}